// What the read-only phone layout shows, computed from the same rows and helpers the desktop
// uses, so a phone and a laptop never disagree about a forecast. Pure: no React, no DOM.

import { orderForecasts } from "../forecast/orderForecasts.js";
import { mergeByReceivedAt, splitForecastLists } from "../forecast/forecastLists.js";
import { liveContractMomentOf } from "../forecast/reportPointer.js";
import { extractMultiway } from "../forecast/multiway.js";
import { briefLlmInputsOf } from "../report/briefLlmInputs.js";
import { buildBrief, watchLineOf } from "../report/brief.js";
import { deriveSignposts } from "../timeline/signposts.js";
import { resolutionMomentLocal } from "../formatDates.js";

/**
 * The phone's forecast list: Watchlist, then Recent, in the order the desktop Forecasts panel
 * uses (the person's own ordering, owned and shared rows together).
 * @param {{forecasts: any[], shared?: any[], order?: string[]|null, isWatchlisted: (f: any) => boolean}} args
 * @returns {{watchlist: any[], recent: any[]}}
 */
export function phoneSections({ forecasts, shared = [], order = null, isWatchlisted }) {
  const merged = mergeByReceivedAt(forecasts || [], shared || []);
  return splitForecastLists(orderForecasts(merged, order), isWatchlisted);
}

/**
 * When the forecast on screen was produced: the newest saved vintage, or this session's
 * update when it is newer (rows do not carry `lastReforecastedAt` across a reload).
 * @returns {string|null} ISO instant
 */
export function updatedAtOf(f) {
  const saved = liveContractMomentOf(f);
  const session = f?.lastReforecastedAt;
  if (!session) return saved;
  if (!saved) return session;
  return Date.parse(session) > Date.parse(saved) ? session : saved;
}

/** "Jan 20, 2029" — a bare date reads as that calendar day everywhere. */
export function longDate(iso) {
  if (!iso) return "";
  const s = String(iso);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** "Resolves Jan 2029" (or "Resolves Nov 3" this year); "Closed …" once the date has passed. */
export function resolvesText(f, now = new Date()) {
  const raw = f?.contract?.resolution_date;
  if (!raw) return "";
  const s = String(raw);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === now.getFullYear();
  const text = d.toLocaleDateString(
    "en-US",
    sameYear ? { month: "short", day: "numeric" } : { month: "short", year: "numeric" },
  );
  return d.getTime() < now.getTime() ? `Closed ${text}` : `Resolves ${text}`;
}

/** The question as asked (preprocessed), for headings. */
export function questionOf(f) {
  return f?.contract?.question || f?.contractQuestion || f?.question || f?.contract?.title || "";
}

/** A short name for the top bar: the contract title when it is shorter than the question. */
export function shortTitleOf(f) {
  const q = questionOf(f);
  const t = String(f?.contract?.title || "").trim();
  return t && t.length < q.length ? t : q;
}

// Markdown the assessment can carry, flattened to running text.
function plainText(md) {
  return String(md || "")
    .split("\n")
    .filter((line) => !/^\s*#{1,6}\s/.test(line))
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+\.)\s+/, ""))
    .join(" ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** The opening of a passage: whole sentences, up to about `max` characters (at least one). */
export function openingSentences(text, max = 260) {
  const flat = plainText(text);
  if (!flat) return "";
  const sentences = flat.match(/[^.!?]+(?:[.!?]+["”’)]*|$)/g) || [flat];
  let out = "";
  for (const s of sentences) {
    const next = (out + " " + s.trim()).trim();
    if (out && next.length > max) break;
    out = next;
    if (out.length >= max) break;
  }
  return out;
}

/**
 * What the brief's bottom line is written from, read off the forecast the same way the report
 * does (so the written lines are the report's own, from the same cache), plus the "Watch" line
 * for the next upcoming trigger. Null when the forecast has no report to read.
 * @param {any} forecast
 * @param {Date} [today]
 * @returns {null | {question: string, narrative: string, uncertainties: string[], watch: string}}
 */
export function bottomLineSourceOf(forecast, today = new Date()) {
  const inputs = briefLlmInputsOf(forecast);
  if (!inputs) return null;
  const r = forecast.report || forecast.serverContext?.report || null;
  const probability =
    typeof r?.probability === "number"
      ? r.probability
      : typeof forecast.contract?.probability === "number"
        ? forecast.contract.probability
        : null;
  const b = buildBrief({
    probability,
    buckets: extractMultiway(forecast)?.buckets ?? null,
    events: forecast.events || [],
    today,
  });
  return { ...inputs.bottomLine, watch: b.next ? watchLineOf(b) : "" };
}

/**
 * The bottom line's rows: the written "Why" and "The risk" when they have arrived, else the
 * assessment's opening and its first open question; then "Watch".
 * @param {ReturnType<typeof bottomLineSourceOf>} source
 * @param {{why?: string, risk?: string}|null} written
 * @returns {Array<{label: string, text: string}>}
 */
export function bottomLineRows(source, written) {
  if (!source) return [];
  const why = written?.why || openingSentences(source.narrative);
  const risk = written?.risk || (source.uncertainties || [])[0] || "";
  return [
    why && { label: "Why", text: why },
    risk && { label: "The risk", text: risk },
    source.watch && { label: "Watch", text: source.watch },
  ].filter(Boolean);
}

/**
 * The triggers that would move the forecast, biggest move first (deriveSignposts' order), and
 * how many have already settled.
 * @param {any} forecast
 * @param {{todayMs: number, concluded?: boolean, resolutions?: object|null}} opts
 */
export function phoneSignposts(forecast, { todayMs, concluded = false, resolutions = null }) {
  const contract = forecast?.contract;
  const resMoment = contract?.resolution_date
    ? resolutionMomentLocal(contract.resolution_date, forecast?.question)
    : null;
  const resolutionMs = resMoment && !Number.isNaN(resMoment.getTime()) ? resMoment.getTime() : NaN;
  const probability = typeof contract?.probability === "number" ? contract.probability : null;
  return deriveSignposts(forecast?.events, {
    todayMs,
    resolutionMs,
    probability,
    resolutions,
    concluded,
  });
}
